// src/components/Projects.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { HiArrowRight, HiArrowLeft } from "react-icons/hi"; // Importing HiArrowLeft for the 'Show Less' button
import project1 from "../assets/ecommerce.png";
import project2 from "../assets/healthcare.png";
import project3 from "../assets/chat-app.png";
import project4 from "../assets/sales.png";
import project5 from "../assets/customer.png";
import project6 from "../assets/internal.png";
import project7 from "../assets/custom.png";
import project8 from "../assets/portal.png";
import project9 from "../assets/automated.png";
import project10 from "../assets/aiagent.png";
import project11 from "../assets/copilot.png";
import project12 from "../assets/predective.png";

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeFilter, setActiveFilter] = useState("all");
  const [showAllProjects, setShowAllProjects] = useState(false); // New state to manage showing all projects

  // Updated Project Data (kept as is from previous iteration)
  const allProjects = [
    // Web Development Projects
    {
      id: 1,
      title: "E-commerce Platform",
      description:
        "A robust online store with secure payment gateways and intuitive user experience.",
      category: "web-development",
      image: project1,
      technologies: ["React", "Django"],
      link: "#",
    },
    {
      id: 2,
      title: "Healthcare Management System",
      description:
        "Comprehensive system for patient records, appointments, and hospital administration.",
      category: "web-development",
      image: project2,
      technologies: ["React", "Node.js"],
      link: "#",
    },
    {
      id: 3,
      title: "Real-time Chat Application",
      description:
        "An interactive chat platform with instant messaging and group chat features.",
      category: "web-development",
      image: project3,
      technologies: ["React", "Node.js", "Express", "Socket.io"],
      link: "#",
    },
    // Mobile Apps Projects
    {
      id: 4,
      title: "Sales & Marketing Application",
      description:
        "Mobile solution for sales teams to manage leads, track performance, and automate marketing tasks.",
      category: "mobile-apps",
      image: project4,
      technologies: ["React Native", "Firebase"],
      link: "#",
    },
    {
      id: 5,
      title: "Customer Feedback Application",
      description:
        "Empowering customers to provide instant feedback and improve service quality.",
      category: "mobile-apps",
      image: project5,
      technologies: ["Flutter", "AWS Amplify"],
      link: "#",
    },
    {
      id: 6,
      title: "Internal Employee Banking App",
      description:
        "Secure mobile banking platform designed for internal employee financial management.",
      category: "mobile-apps",
      image: project6,
      technologies: ["React Native", "Node.js", "MongoDB"],
      link: "#",
    },
    // Power Apps Projects
    {
      id: 7,
      title: "Custom Website for Standalone Org.",
      description:
        "Developed bespoke web solutions tailored to unique organizational requirements using Power Pages.",
      category: "power-apps",
      image: project7,
      technologies: ["Power Pages", "Dataverse"],
      link: "#",
    },
    {
      id: 8,
      title: "Public Portal for UK Client",
      description:
        "Designed and implemented a public-facing portal for a UK-based client, enhancing external engagement.",
      category: "power-apps",
      image: project8,
      technologies: ["Power Pages", "Dataverse", "SharePoint"],
      link: "#",
    },
    {
      id: 9,
      title: "Automated Workflow Management",
      description:
        "Streamlined internal processes with custom Power Apps Canvas Apps and integrations.",
      category: "power-apps",
      image: project9,
      technologies: ["Power Apps Canvas Apps", "Dataverse", "Power Automate"],
      link: "#",
    },
    // AI Solutions Projects
    {
      id: 10,
      title: "AI Agents Copilot Integration",
      description:
        "Integrated AI-powered copilots to enhance business operations and decision-making.",
      category: "ai-solutions",
      image: project10,
      technologies: ["Microsoft Copilot Studio", "Azure AI"],
      link: "#",
    },
    {
      id: 11,
      title: "Copilot Virtual Agents",
      description:
        "Developed intelligent virtual agents using Microsoft Copilot for automated customer support.",
      category: "ai-solutions",
      image: project11,
      technologies: ["Microsoft Copilot Studio", "NLP"],
      link: "#",
    },
    {
      id: 12,
      title: "Predictive Analytics Dashboard",
      description:
        "An AI-driven dashboard providing predictive insights for market trends and business forecasting.",
      category: "ai-solutions",
      image: project12,
      technologies: ["Python", "TensorFlow", "Power BI"],
      link: "#",
    },
  ];

  // Updated Filters (kept as is)
  const filters = [
    { key: "all", label: "All Projects" },
    { key: "web-development", label: "Web Development" },
    { key: "mobile-apps", label: "Mobile Apps" },
    { key: "power-apps", label: "Power Apps" },
    { key: "ai-solutions", label: "AI Solutions" },
  ];

  // Filter projects based on activeFilter
  const filteredProjects =
    activeFilter === "all"
      ? allProjects
      : allProjects.filter((project) => project.category === activeFilter);

  // Determine which projects to display
  const projectsToDisplay = showAllProjects
    ? filteredProjects
    : filteredProjects.slice(0, 6);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <section
      id="projects"
      className="section-padding bg-neutral-50 relative overflow-hidden"
    >
      {" "}
      {/* Changed bg-gray-50 to bg-neutral-50 */}
      {/* Enhanced Background Elements (kept from original for visual consistency) */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 -right-40 w-96 h-96 bg-secondary-100 rounded-full opacity-25"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 270, 0],
            x: [0, 60, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -bottom-20 -left-40 w-80 h-80 bg-primary-100 rounded-full opacity-30"
          animate={{
            scale: [1, 1.4, 1],
            rotate: [360, 90, 0],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 24,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4,
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-72 h-72 bg-accent-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
            x: [0, 35, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 7,
          }}
        />

        {/* Project-themed floating elements */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-4 h-4 bg-secondary-300 rounded-lg opacity-40"
            style={{
              left: `${10 + i * 12}%`,
              top: `${15 + i * 10}%`,
            }}
            animate={{
              y: [-30, 30, -30],
              x: [-20, 20, -20],
              opacity: [0.2, 0.8, 0.2],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 12 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1,
            }}
          />
        ))}
      </div>
      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            whileInView={{
              scale: [0.9, 1.05, 1],
              opacity: [0, 1, 1],
            }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-6" // Changed text-gray-900 to text-neutral-900
          >
            Our <span className="text-gradient">Projects</span>
          </motion.h2>
          <div className="divider-gradient mb-6"></div>
          <motion.p
            variants={itemVariants}
            whileInView={{
              y: [30, 0],
              opacity: [0, 1],
            }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-neutral-700 max-w-3xl mx-auto mb-12" // Changed text-gray-600 to text-neutral-700
          >
            Explore our portfolio of successful projects that showcase our
            expertise and commitment to excellence.
          </motion.p>

          {/* Filter Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {filters.map((filter) => (
              <motion.button
                key={filter.key}
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 4px 15px rgba(0, 123, 255, 0.1)", // Updated rgba to #007bff
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setActiveFilter(filter.key);
                  setShowAllProjects(false); // Reset showAllProjects when filter changes
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter.key
                    ? "bg-primary-600 text-white shadow-lg"
                    : "bg-white text-neutral-700 hover:bg-primary-50 hover:text-primary-600" // Changed text-gray-700 to text-neutral-700, hover:bg-primary-50, hover:text-primary-600
                }`}
              >
                {filter.label}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {projectsToDisplay.map(
            (
              project // Use projectsToDisplay here
            ) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                // Minimal hover effect: very slight scale and shadow
                whileHover={{
                  scale: 1.005, // Even smaller scale for very minimal hover
                  boxShadow: "0 3px 10px rgba(0, 123, 255, 0.05)", // Even lighter shadow, updated rgba
                }}
                transition={{
                  duration: 0.2, // Faster transition for smoother feel
                  ease: "easeOut",
                }}
                className="card group cursor-pointer overflow-hidden rounded-lg bg-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                {/* Project Image */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-102" // Slightly reduced image scale on hover
                  />
                  {/* Minimal Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Project Info */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2 text-center">
                    {" "}
                    {/* Changed text-gray-900 to text-neutral-900 */}
                    {project.title}
                  </h3>
                  <p className="text-neutral-700 text-sm mb-4 leading-relaxed text-center">
                    {" "}
                    {/* Changed text-gray-600 to text-neutral-700 */}
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    {project.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full" // Updated colors
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )
          )}
        </motion.div>

        {/* Show More/Show Less Buttons Container */}
        <div className="flex justify-center mt-12">
          {/* Show More Button */}
          {filteredProjects.length > 6 && !showAllProjects && (
            <motion.button
              onClick={() => setShowAllProjects(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300"
            >
              <span>Show More Projects</span>
              <HiArrowRight className="w-5 h-5" />
            </motion.button>
          )}

          {/* Show Less Button */}
          {filteredProjects.length > 6 && showAllProjects && (
            <motion.button
              onClick={() => setShowAllProjects(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-neutral-600 hover:bg-neutral-700 text-white px-8 py-3 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300"
            >
              <HiArrowLeft className="w-5 h-5" />
              <span>Show Less Projects</span>
            </motion.button>
          )}
        </div>
      </div>
    </section>
  );
};

export default Projects;
